import { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { useAuth } from '../context/AuthContext';
const BLOCKED_SETTINGS_TABS = ['Email Setup', 'Team', 'Menu Permissions', 'Notifications'];
const PASSWORD_EXPIRY_DAYS = 90;
const EXPIRY_WARNING_DAYS = 10;
const PW_BANNER_SESSION_KEY = 'pw_expiry_banner_dismissed';

export default function PortalLayout() {
  const { isAuthenticated, refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(PW_BANNER_SESSION_KEY) === '1'
  );
  const { pathname, search } = useLocation();

  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to upgrade page when backend returns 402 (plan expired / inactive),
  // but skip the redirect if the user is already on an allowed page (upgrade-plan
  // itself, or a non-blocked settings tab like Billing).
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') ?? '';
      const onUpgradePage = window.location.pathname === '/app/upgrade-plan';
      const onAllowedSettings =
        window.location.pathname === '/app/settings' &&
        !BLOCKED_SETTINGS_TABS.includes(tab);
      if (!onUpgradePage && !onAllowedSettings) {
        navigate('/app/upgrade-plan', { replace: true });
      }
    };
    window.addEventListener('auth:plan-expired', handler);
    return () => window.removeEventListener('auth:plan-expired', handler);
  }, [navigate]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const activeTab = new URLSearchParams(search).get('tab');

  // --- Plan expiry guard ---
  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);

  const isPlanDeactivated =
    user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';

  const isPlanExpired = isTrialExpired || isPlanDeactivated;

  const isBlockedByPlan =
    pathname !== '/app/upgrade-plan' &&
    (pathname !== '/app/settings' || BLOCKED_SETTINGS_TABS.includes(activeTab ?? ''));

  if (isPlanExpired && isBlockedByPlan) {
    return <Navigate to="/app/upgrade-plan" replace />;
  }

  // --- Password expiry guard (only if trial is not expired) ---
  const daysSincePwChange = user?.securityAlertsEnabled && user?.passwordChangedAt
    ? (Date.now() - new Date(user.passwordChangedAt).getTime()) / 86_400_000
    : 0;

  const isPasswordExpired = user?.securityAlertsEnabled && daysSincePwChange >= PASSWORD_EXPIRY_DAYS;

  const daysUntilExpiry = (() => {
    if (!user?.securityAlertsEnabled || !user?.passwordChangedAt) return null;
    const remaining = PASSWORD_EXPIRY_DAYS - daysSincePwChange;
    return remaining > 0 && remaining <= EXPIRY_WARNING_DAYS ? Math.ceil(remaining) : null;
  })();

  const isOnAccountTab =
    pathname === '/app/settings' && (!activeTab || activeTab === 'Account');

  if (isPasswordExpired && !isPlanExpired && !isOnAccountTab) {
    return <Navigate to="/app/settings?tab=Account&require_password_change=true" replace />;
  }

  const handleDismissBanner = () => {
    sessionStorage.setItem(PW_BANNER_SESSION_KEY, '1');
    setBannerDismissed(true);
  };

  return (
    <div className="portal-wrap">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && (
        <div
          className="portal-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <div className="portal-main">
        <TopBar onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        {daysUntilExpiry !== null && !bannerDismissed && (
          <div className="pw-expiry-warning-banner">
            <ShieldAlert size={18} />
            <span>
              Your password expires in <strong>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</strong>.{' '}
              <Link to="/app/settings?tab=Account">Change it now</Link> to avoid being locked out.
            </span>
            <button onClick={handleDismissBanner} aria-label="Dismiss">✕</button>
          </div>
        )}
        <main className="portal-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
